import { Stripe, StripeElements } from '@stripe/stripe-js';
import { push } from 'connected-react-router';
import React, { Fragment, useEffect } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../../../components/Loading';
import PageTitle from '../../../../components/PageTitle';
import { REPOSIT_PAYMENT_WARNING } from '../../../../constants/reposit';
import { createLoadingSelector } from '../../../../redux/loading/loading.selector';
import {
  checkoutPaymentExited,
  payRequested,
  PAY_STORE_KEY,
  pollPaymentSuccessCancelled,
  prepareCheckoutPaymentRequested,
  PREPARE_CHECKOUT_PAYMENT_STORE_KEY,
} from '../../../../redux/order-customer-actions/order-customer-actions.actions';
import {
  getIsPollingForPaymentCompletion,
  getPaymentIntentSecret,
} from '../../../../redux/order-customer-actions/order-customer-actions.selectors';
import { PaymentType } from '../../../../redux/order-customer-actions/order-customer-actions.types';
import { getCurrentOrderCustomer } from '../../../../redux/order/order.selectors';
import PaymentContainer from '../../../Payment';

interface PaymentProps {}

const WarningMessage = () => {
  return <span>{REPOSIT_PAYMENT_WARNING}</span>;
};

const PAYMENT_COMPLETION_TIMEOUT_MS = 30000;

const Payment: React.FC<PaymentProps> = (props) => {
  const dispatch = useDispatch();
  const { order, customer, fee } = useSelector(getCurrentOrderCustomer);
  const paymentIntentSecret = useSelector(getPaymentIntentSecret);
  const preparePaymentLoadingSelector = createLoadingSelector([PREPARE_CHECKOUT_PAYMENT_STORE_KEY]);
  const isPreparingPayment = useSelector(preparePaymentLoadingSelector);
  const getPayRepositLoadingSelector = createLoadingSelector([PAY_STORE_KEY]);
  const isPayRepositLoading = useSelector(getPayRepositLoadingSelector);
  const submitPayment = (stripe: Stripe, elements: StripeElements) =>
    dispatch(payRequested({ stripe, paymentIntentSecret, type: PaymentType.REPOSIT, elements }));
  const isPolling = useSelector(getIsPollingForPaymentCompletion);

  // Preparing the payment checks for one already in flight (for example after a mobile browser reload
  // during 3DS) before the card form is shown; leaving the step cancels that check and any completion poll.
  useEffect(() => {
    dispatch(prepareCheckoutPaymentRequested(order.id, customer.id));
    return () => {
      dispatch(checkoutPaymentExited());
    };
  }, [dispatch, order.id, customer.id]);

  // The webhook that completes the pay action can lag behind Stripe. If it has not arrived within the
  // timeout, stop waiting here and let the finish page explain that the payment is still processing.
  useEffect(() => {
    if (!isPolling) return;
    const timeoutId = setTimeout(() => {
      dispatch(pollPaymentSuccessCancelled());
      dispatch(
        push(`/checkout/${order.id}/${customer.id}/finish`, {
          waitingPayment: true,
        })
      );
    }, PAYMENT_COMPLETION_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [dispatch, isPolling, order.id, customer.id]);

  const isLoading = isPreparingPayment || isPolling || isPayRepositLoading;
  // Hidden rather than disabled while an existing payment may be resumed, so the tenant cannot start a second one.
  const showCardForm = fee && !isPreparingPayment && !isPolling;

  return (
    <Fragment>
      <Container>
        <Row>
          <Col lg={10} push={{ lg: 1 }}>
            <PageTitle>Payment</PageTitle>
          </Col>
        </Row>
      </Container>
      {showCardForm ? (
        <PaymentContainer
          amount={`${fee}`}
          warningMessage={WarningMessage}
          isSubmitting={isLoading}
          submitCard={submitPayment}
          type={PaymentType.REPOSIT}
          title="Your share of this Reposit is"
          paymentMode={'payment'}
        />
      ) : (
        <Loading />
      )}
    </Fragment>
  );
};

export default Payment;
